import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react'; // useRef hozzáadva
import apiService from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Ez az állapot jelzi, hogy az autentikáció inicializálása folyamatban van
  error: null,
  validationErrors: null
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  UPDATE_USER: 'UPDATE_USER',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_VALIDATION_ERRORS: 'SET_VALIDATION_ERRORS'
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        validationErrors: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false, // Sikeres bejelentkezés után a betöltés befejeződött
        error: null,
        validationErrors: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false, // Sikertelen bejelentkezés után a betöltés befejeződött
        error: action.payload.message,
        validationErrors: action.payload.errors
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false, // Kijelentkezés után a betöltés befejeződött
        error: null,
        validationErrors: null
      };
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false // Felhasználó beállítása után a betöltés befejeződött
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        isAuthenticated: true
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      if (state.error === null && state.validationErrors === null) {
        return state;
      }
      return {
        ...state,
        error: null,
        validationErrors: null
      };

    case AUTH_ACTIONS.SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: action.payload
      };
    
    default:
      return state;
  }
}

// Context
const AuthContext = createContext();

// Provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const isAuthInitialized = useRef(false); // ÚJ: Ref az autentikáció inicializálásának követésére

  // Token kezelés
  const setToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
      apiService.setToken(token);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      apiService.setToken(null);
    }
  };

  // Inicializálás - token ellenőrzése
  useEffect(() => {
    // ÚJ: Csak akkor futtassuk az initializeAuth-t, ha még nem inicializáltuk
    if (isAuthInitialized.current) {
      return;
    }

    const initializeAuth = async () => {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      try {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        
        if (token && userStr) {
          apiService.setToken(token);
          
          try {
            const response = await apiService.get(API_ENDPOINTS.AUTH.ME);
            
            if (response.success) {
              dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.data.user });
              localStorage.setItem('user', JSON.stringify(response.data.user));
            } else {
              throw new Error('Invalid token');
            }
          } catch (error) {
            setToken(null);
            dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null });
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: null });
      } finally {
        // Fontos: Még ha hiba is történik, az inicializálás befejeződött
        isAuthInitialized.current = true; // ÚJ: Jelöljük, hogy az inicializálás megtörtént
      }
    };

    initializeAuth();
  }, []); // Üres függőségi tömb, egyszer fut le mountoláskor

  // Bejelentkezés
  const login = async (email, password, rememberMe = false) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    
    try {
      const response = await apiService.post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password
      });
      
      if (response.success && response.data.token) {
        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.data.user });
        
        return {
          success: true,
          message: response.message || 'Sikeres bejelentkezés',
          user: response.data.user
        };
      } else {
        if (response.errors) {
          throw Object.assign(new Error(response.message || 'Bejelentkezés sikertelen'), { errors: response.errors });
        } else {
          throw new Error(response.message || 'Bejelentkezés sikertelen');
        }
      }
    } catch (error) {
      const errorMessage = error.message || 'Bejelentkezés sikertelen';
      const detailedErrors = error.errors || null;
      const unverified = error.data?.unverified || false;

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { message: errorMessage, errors: detailedErrors }
      });

      return {
        success: false,
        error: errorMessage,
        detailedErrors: detailedErrors,
        unverified
      };
    }
  };

  // Regisztráció
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    console.log('➡️ AuthContext register function called with userData:', userData);
    
    try {
      const apiData = userData;

      console.log('➡️ Sending API request to:', API_ENDPOINTS.AUTH.REGISTER, 'with data:', apiData);
      const response = await apiService.post(API_ENDPOINTS.AUTH.REGISTER, apiData);
      console.log('✅ API response received:', response);
      
      if (response.success) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
        
        return {
          success: true,
          message: response.message || 'Sikeres regisztráció',
        };
      } else {
        if (response.errors) {
          throw Object.assign(new Error(response.message || 'Regisztráció sikertelen'), { errors: response.errors });
        } else {
          throw new Error(response.message || 'Regisztráció sikertelen');
        }
      }
    } catch (error) {
      const errorMessage = error.message || 'Regisztráció sikertelen';
      const detailedErrors = error.details || null;

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { message: errorMessage, errors: detailedErrors }
      });

      return {
        success: false,
        error: errorMessage,
        errorStatus: error.status || null,
        detailedErrors: detailedErrors
      };
    }
  };

  // Kijelentkezés
  const logout = async () => {
    try {
      await apiService.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Felhasználó frissítése
  const updateUser = (userData) => {
    console.log('🔄 Updating user in context:', userData);
    
    if (userData && typeof userData === 'object') {
      const updatedUser = { ...state.user, ...userData };
      
      dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: userData });
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      console.log('✅ User updated in context:', updatedUser);
      return updatedUser;
    }
  };

  // Felhasználó újratöltése a backend-ről
  const refreshUser = async () => {
    try {
      const response = await apiService.get(API_ENDPOINTS.AUTH.ME);
      
      if (response.success) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.data.user });
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data.user;
      }
      
      throw new Error('Felhasználó adatok lekérése sikertelen');
    } catch (error) {
      console.error('Refresh user error:', error);
      throw error;
    }
  };

  // Google OAuth bejelentkezés
  const loginWithGoogle = async (credential) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const response = await apiService.post(API_ENDPOINTS.AUTH.GOOGLE, { accessToken: credential });
      if (response.success && response.status === 'new_social_user') {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return { success: true, newUser: true, profile: response.profile };
      }
      if (response.success && response.data?.token) {
        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.data.user });
        return { success: true, newUser: false };
      }
      throw new Error(response.message || 'Google bejelentkezés sikertelen');
    } catch (error) {
      const errorMessage = error.message || 'Google bejelentkezés sikertelen';
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: { message: errorMessage, errors: null } });
      return { success: false, error: errorMessage };
    }
  };

  // Facebook OAuth bejelentkezés
  const loginWithFacebook = async (accessToken, userID) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const response = await apiService.post(API_ENDPOINTS.AUTH.FACEBOOK, { accessToken, userID });
      if (response.success && response.status === 'new_social_user') {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return { success: true, newUser: true, profile: response.profile };
      }
      if (response.success && response.data?.token) {
        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.data.user });
        return { success: true, newUser: false };
      }
      throw new Error(response.message || 'Facebook bejelentkezés sikertelen');
    } catch (error) {
      const errorMessage = error.message || 'Facebook bejelentkezés sikertelen';
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: { message: errorMessage, errors: null } });
      return { success: false, error: errorMessage };
    }
  };

  // Social regisztráció befejezése
  const completeSocialRegistration = async (profileData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const response = await apiService.post(API_ENDPOINTS.AUTH.SOCIAL_COMPLETE, profileData);
      if (response.success && response.data?.token) {
        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: response.data.user });
        return { success: true };
      }
      throw new Error(response.message || 'Regisztráció sikertelen');
    } catch (error) {
      const errorMessage = error.message || 'Regisztráció sikertelen';
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: { message: errorMessage, errors: null } });
      return { success: false, error: errorMessage };
    }
  };

  // Hiba törlése
  const clearError = useCallback(() => {
    if (state.error !== null || state.validationErrors !== null) {
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
    }
  }, [state.error, state.validationErrors, dispatch]);

  // Helper functions
  const isAdmin = () => state.user?.userType === 'ADMIN';
  const isProvider = () => state.user?.userType === 'PROVIDER';
  const isClient = () => state.user?.userType === 'CLIENT';

  const value = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading, // Ez az isLoading állapot, amit a ProtectedRoute használ
    error: state.error,
    validationErrors: state.validationErrors,
    
    // Actions
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    clearError,
    loginWithGoogle,
    loginWithFacebook,
    completeSocialRegistration,
    
    // Helpers
    isAdmin,
    isProvider,
    isClient
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;